import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import { notification } from 'ant-design-vue';
import router from '@/router';
import i18n from '@/i18n';
import CMSUser from '@/models/CMSUser';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import Schedule from '@/models/Schedule';
import ScheduleRepository from '@/repositories/ScheduleRepository';
import Resource from '@/models/Resource';
import CalendarScheduleEdit from '@/modules/CMS/components/CalendarScheduleEdit.vue';
import { RouteNames } from '@/enums/routes/RouteNames';
import ResourceRepository from '@/repositories/ResourceRepository';
import Rule from '@/models/Rule';


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarSchedules',
  setup(__props) {

const loading = ref(false);
const selectedUser = ref('');
const selectedResource = ref('');
const activeTab = ref('users');

const fields = computed(() => [
    {
        title: 'ID',
        key: 'id',
        dataIndex: 'id',
        sortable: true,
    },
    activeTab.value === 'users'
        ? {
              title: i18n.t('User'),
              key: 'userId',
              dataIndex: 'userId',
              sortable: true,
              scopedSlots: {
                  customRender: 'user',
              },
          }
        : {
              title: i18n.t('Resource'),
              key: 'resourceId',
              dataIndex: 'resourceId',
              sortable: true,
              scopedSlots: {
                  customRender: 'resource',
              },
          },
    {
        title: i18n.t('Naziv'),
        key: 'title',
        dataIndex: 'title',
        sortable: true,
    },
    {
        title: i18n.t('Akcije'),
        key: 'actions',
        sortable: false,
        scopedSlots: {
            customRender: 'actions',
        },
    },
]);

const users = computed(() => CMSUserRepository.getAll());
const resources = computed(() => ResourceRepository.getAll());
const schedules = computed(() => ScheduleRepository.getAll());

async function retrieveAllSchedules() {
    if (selectedUser.value && selectedUser.value !== '' && activeTab.value === 'users') {
        await getCalendarUserSchedules();
        return;
    }

    if (selectedResource.value && selectedResource.value !== '' && activeTab.value === 'resources') {
        await getCalendarResourceSchedules();
        return;
    }

    try {
        await Schedule.getAll();
    } catch (e) {
        return Promise.reject(e);
    } finally {
        loading.value = false;
    }
}

async function getCalendarUserSchedules() {
    if (!selectedUser.value || selectedUser.value === '') {
        await retrieveAllSchedules();
        return;
    }
    loading.value = true;
    try {
        await Schedule.getByUserId(selectedUser.value);
        loading.value = false;
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
        loading.value = false;
        return Promise.reject();
    }
}

async function getCalendarResourceSchedules() {
    if (!selectedResource.value || selectedResource.value === '') {
        await retrieveAllSchedules();
        return;
    }
    loading.value = true;
    try {
        await Schedule.getByResourceId(selectedResource.value);
        loading.value = false;
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
        loading.value = false;
        return Promise.reject();
    }
}

function getUserName(id: number) {
    if (!users.value || !id) {
        return '-';
    }
    return users.value.find((user) => user.id === id.toString())?.name ?? '-';
}

function getResourceName(id: string) {
    if (!resources.value || !id) {
        return '-';
    }
    return resources.value.find((resource) => resource.id === id)?.title ?? '-';
}

async function onTabChange(tab: string) {
    activeTab.value = tab;
    await retrieveAllSchedules();
}

async function deleteSchedule(id: string) {
    loading.value = true;
    await Schedule.deleteExisting(id);

    notification.success({
        message: i18n.t('Schedule successfully deleted!') as string,
        description: '',
    });

    retrieveAllSchedules();
}

function editItem(item: Schedule) {
    const type = item.userId !== null ? 'user' : item.resourceId ? 'resource' : null;
    const id = item.id;
    if (id) {
        router.push({
            name: RouteNames.schedulesResources,
            params: { id: id.toString(), dynamic: 'CalendarScheduleEdit' },
            query: { type },
        });
    }
}

function addUserItem() {
    router.push({
        name: RouteNames.schedulesResources,
        params: { dynamic: 'CalendarScheduleEdit' },
        query: { type: 'user', userId: selectedUser.value },
    });
}

function addResourceItem() {
    router.push({
        name: RouteNames.schedulesResources,
        params: { dynamic: 'CalendarScheduleEdit' },
        query: { type: 'resource', resourceId: selectedResource.value },
    });
}

onMounted(async () => {
    try {
        loading.value = true;
        await Promise.all([CMSUser.getAll(), Resource.getAll(), retrieveAllSchedules()]);
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
    } finally {
        loading.value = false;
    }

    await Rule.getAll();
});

return { __sfc: true,loading, selectedUser, selectedResource, activeTab, fields, users, resources, schedules, retrieveAllSchedules, getCalendarUserSchedules, getCalendarResourceSchedules, getUserName, getResourceName, onTabChange, deleteSchedule, editItem, addUserItem, addResourceItem }
}

})